import { ReactNode, useEffect, useState } from "react";
import {
  Link as BaseLink,
  Outlet,
  ShouldRevalidateFunction,
  useNavigate,
  useSearchParams,
} from "react-router";
import { generatePath, To } from "react-router";
import { assumeNullish, invariant } from "@/utils/helpers";
import EntityProfilePic from "@/components/EntityProfilePic";
import { Badge } from "@/kit/ui/badge";
import {
  ArrowUpRightIcon,
  ChevronDownIcon,
  Copy01Icon,
  EyeIcon,
  FileCode01Icon,
  Lock01Icon,
  PlusIcon,
} from "@/icons";
import { FormattedMessage, useIntl } from "react-intl";
import VoteDisplay from "@/components/VoteDisplay";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import NavItem from "@/components/NavItem";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuItem,
  ResponsiveMenuTrigger,
} from "@/kit/ui/responsive-menu";
import { Container } from "@/components/Container";
import { MetaLayout } from "@/common/MetaLayout";
import { HeaderContainer } from "@/components/HeaderContainer";
import VersionBadge from "@/components/VersionBadge";
import { WorkspacePublishModal } from "@/components/WorkspacePublishButton";
import WorkspaceVersionCloneButton from "@/components/WorkspaceVersionCloneButton";
import WorkspaceVersionHistory from "@/components/WorkspaceVersionHistory";
import { useLocation } from "@/utils/location";
import { splitWorkspaceSlug, getOgImageUrl } from "@/utils/routing";
import { Environment, graphql, useFragment } from "react-relay";
import { safeParse } from "valibot";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import {
  WorkspacePageParams,
  WorkspaceVersionPageParams,
} from "@/utils/workspace";
import { WorkspaceScaffoldQuery as WorkspaceScaffoldQueryType } from "./__generated__/WorkspaceScaffoldQuery.graphql";
import { WorkspaceScaffoldVersionQuery as WorkspaceScaffoldVersionQueryType } from "./__generated__/WorkspaceScaffoldVersionQuery.graphql";
import { WorkspaceScaffoldWorkspaceFragment$key } from "./__generated__/WorkspaceScaffoldWorkspaceFragment.graphql";
import { WorkspaceScaffoldVersionFragment$key } from "./__generated__/WorkspaceScaffoldVersionFragment.graphql";

export default function WorkspacesScaffold(): ReactNode {
  const intl = useIntl();
  const {
    query: { workspaceBySlug },
    workspace,
  } = useWorkspaceScaffoldQuery();
  const location = useLocation();
  if (!workspaceBySlug || !workspace) {
    return <Outlet />;
  }
  const [owner, slug] = workspace.slug.split("/", 2);
  return (
    <>
      <MetaLayout
        metaImageUrl={getOgImageUrl(
          {
            type: "workspace",
            owner,
            slug,
          },
          location.origin,
        )}
        metaImageAlt={intl.formatMessage(
          { defaultMessage: "{name} on aqora" },
          { name: workspace.name ?? "Workspace" },
        )}
      />
      <HeaderContainer>
        <Header />
        <Navigator />
      </HeaderContainer>

      <Container>
        <Outlet />
      </Container>
    </>
  );
}
function Header() {
  return (
    <div className="flex flex-wrap md:flex-nowrap gap-4">
      <HeaderTitle />
      <HeaderActions />
    </div>
  );
}
function HeaderTitle() {
  const { workspace, version, latestVersion } = useWorkspaceScaffoldQuery();
  invariant(!!workspace);
  const intl = useIntl();
  return (
    <div className="flex-1 basis-full md:basis-auto flex flex-wrap md:flex-nowrap items-center gap-2">
      <p className="text-dsm font-semibold inline-flex items-center gap-2">
        <BaseLink
          to={generatePath("/:username", {
            username: workspace.owner.username,
          })}
          className="inline-flex items-center gap-2"
        >
          <EntityProfilePic
            entity={workspace.owner}
            size="8"
            className="hidden md:block"
          />
          <span className="text-grey-500 font-semibold">
            {workspace.owner.username}
          </span>
        </BaseLink>

        <span>{" / "}</span>

        <span className="text-grey-900">{workspace.name}</span>
      </p>

      {workspace.private ? (
        <Badge hasSuffix className="gap-1">
          <Lock01Icon className="w-3 h-3" />
          <FormattedMessage defaultMessage="Private" />
        </Badge>
      ) : (
        <Badge hasSuffix className="gap-1">
          <EyeIcon className="w-3 h-3" />
          <FormattedMessage defaultMessage="Public" />
        </Badge>
      )}

      {latestVersion && (
        <WorkspaceVersionHistory
          workspace={workspace}
          currentVersion={(version ?? latestVersion).version}
        >
          <VersionBadge
            version={latestVersion.version}
            title={intl.formatMessage({ defaultMessage: "Latest version" })}
          />
        </WorkspaceVersionHistory>
      )}
    </div>
  );
}
function HeaderActions() {
  const { workspace } = useWorkspaceScaffoldQuery();
  invariant(!!workspace);
  return (
    <div className="flex flex-wrap flex-row-reverse md:flex-row gap-2">
      <VoteDisplay subject={workspace} kind="horizontal-upvote" />
      <HeaderVersionAction />
    </div>
  );
}
interface VersionAction {
  key: string;
  icon: ReactNode;
  label: ReactNode;
  /// Navigation actions set `to`; dialog actions set `onSelect`.
  to?: To;
  onSelect?: () => void;
}
function HeaderVersionAction() {
  const { workspace, version, latestVersion, viewer } =
    useWorkspaceScaffoldQuery();
  invariant(!!workspace);
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trackParam = searchParams.get("track");
  // Latch the track referral: switching tabs drops the `?track=` query param,
  // but the scaffold header stays mounted, so keep the last-seen value so the
  // clone action still targets the track.
  const [trackId, setTrackId] = useState<string | null>(trackParam);
  useEffect(() => {
    if (trackParam && trackParam !== trackId) setTrackId(trackParam);
  }, [trackParam, trackId]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const currentVersion = version ?? latestVersion;
  if (!currentVersion) return;
  const isPublished = currentVersion.publishedAt !== null;
  // `latestVersion` is the newest *created* version; the newest draft when
  // one exists, else the latest published version.
  const isNewest = currentVersion.id === latestVersion?.id;
  const openEditor: VersionAction = {
    key: "open-editor",
    icon: <FileCode01Icon />,
    label: <FormattedMessage defaultMessage="Open Editor" />,
    to: generatePath("/workspaces/:id/edit", { id: currentVersion.id }),
  };
  const goToLatest: VersionAction | null = latestVersion
    ? {
        key: "go-to-latest",
        icon: <ArrowUpRightIcon />,
        label: <FormattedMessage defaultMessage="Go to Latest" />,
        to: generatePath("/:owner/:slug/versions/:version", {
          ...splitWorkspaceSlug(workspace.slug),
          version: latestVersion.version,
        }),
      }
    : null;
  const createDraft: VersionAction = {
    key: "create-draft",
    icon: <PlusIcon />,
    label: <FormattedMessage defaultMessage="Create Draft" />,
    onSelect: () => setPublishOpen(true),
  };
  const clone: VersionAction = {
    key: "clone",
    icon: <Copy01Icon />,
    label: <FormattedMessage defaultMessage="Clone" />,
    onSelect: () => setCloneOpen(true),
  };
  // The highest-priority available action is the primary button; the rest go
  // into the attached dropdown.
  const actions: VersionAction[] = [];
  const newestIsDraft = !!latestVersion && latestVersion.publishedAt === null;
  if (workspace.viewerCanCreateVersion) {
    if (!isPublished && currentVersion.viewerCanEdit) {
      actions.push(openEditor);
    }
    // Jump to the newest version (a draft when one exists, else the latest
    // published); owners can read either.
    if (goToLatest && !isNewest) {
      actions.push(goToLatest);
    }
    actions.push(createDraft);
    if (viewer) {
      actions.push(clone);
    }
  } else {
    // Viewers without edit access: Clone is the primary action on published
    // versions (drafts aren't readable by non-owners anyway).
    if (viewer && isPublished) {
      actions.push(clone);
    }
    // Non-owners can't reach a draft, so only offer "Go to Latest" when the
    // newest version is published.
    if (goToLatest && !isNewest && !newestIsDraft) {
      actions.push(goToLatest);
    }
  }
  const [primary, ...secondary] = actions;
  if (!primary) return;
  const primaryClassName = secondary.length > 0 ? "rounded-r-none" : undefined;
  return (
    <>
      <div className="flex flex-row">
        {primary.to ? (
          <Link
            to={primary.to}
            variant="primary"
            className={`text-sm ${primaryClassName ?? ""}`}
          >
            {primary.icon}
            {primary.label}
          </Link>
        ) : (
          <Button
            variant="primary"
            className={`text-sm ${primaryClassName ?? ""}`}
            onClick={primary.onSelect}
          >
            {primary.icon}
            {primary.label}
          </Button>
        )}
        {secondary.length > 0 && (
          <ResponsiveMenu>
            <ResponsiveMenuTrigger asChild>
              <Button
                variant="primary"
                className="rounded-l-none border-l-brand-600 px-2"
                aria-label={intl.formatMessage({
                  defaultMessage: "More actions",
                })}
              >
                <ChevronDownIcon />
              </Button>
            </ResponsiveMenuTrigger>
            <ResponsiveMenuContent align="end">
              {secondary.map((action) => (
                <ResponsiveMenuItem
                  key={action.key}
                  onClick={() =>
                    action.to ? navigate(action.to) : action.onSelect?.()
                  }
                >
                  {action.icon}
                  {action.label}
                </ResponsiveMenuItem>
              ))}
            </ResponsiveMenuContent>
          </ResponsiveMenu>
        )}
      </div>

      <WorkspacePublishModal
        workspace={workspace}
        open={publishOpen}
        onOpenChange={setPublishOpen}
      />
      {viewer && (
        <WorkspaceVersionCloneButton
          version={currentVersion}
          viewer={viewer}
          trackId={trackId}
          open={cloneOpen}
          onOpenChange={setCloneOpen}
        />
      )}
    </>
  );
}
function Navigator() {
  const { workspace, version } = useWorkspaceScaffoldQuery();
  invariant(!!workspace);
  const overviewTo = version
    ? generatePath("/:owner/:slug/versions/:version", {
        ...splitWorkspaceSlug(workspace.slug),
        version: version.version,
      })
    : generatePath("/:owner/:slug", splitWorkspaceSlug(workspace.slug));
  const filesTo = version
    ? generatePath("/:owner/:slug/versions/:version/files", {
        ...splitWorkspaceSlug(workspace.slug),
        version: version.version,
      })
    : generatePath("/:owner/:slug/files", splitWorkspaceSlug(workspace.slug));
  return (
    <ScrollableScaffoldNav className="-mb-px">
      <div className="flex justify-start items-center w-full">
        <NavItem to={overviewTo} end>
          <FormattedMessage defaultMessage="Overview" />
        </NavItem>
        <NavItem to={filesTo}>
          <FormattedMessage defaultMessage="Files" />
        </NavItem>
        <NavItem
          to={generatePath(
            "/:owner/:slug/discussions",
            splitWorkspaceSlug(workspace.slug),
          )}
        >
          <FormattedMessage defaultMessage="Discussions" />
        </NavItem>
        {workspace.submissions.edges.length > 0 && (
          <NavItem
            to={generatePath(
              "/:owner/:slug/submissions",
              splitWorkspaceSlug(workspace.slug),
            )}
          >
            <FormattedMessage defaultMessage="Submissions" />
          </NavItem>
        )}
        {workspace.viewerCanEdit && (
          <NavItem
            to={generatePath(
              "/:owner/:slug/settings",
              splitWorkspaceSlug(workspace.slug),
            )}
          >
            <FormattedMessage defaultMessage="Settings" />
          </NavItem>
        )}
      </div>
    </ScrollableScaffoldNav>
  );
}
const WorkspaceScaffoldQuery = graphql`
  query WorkspaceScaffoldQuery($owner: String!, $slug: String!) {
    viewer @ifAllowed {
      ...WorkspaceVersionCloneButtonViewerFragment
    }
    workspaceBySlug(owner: $owner, slug: $slug) {
      ...WorkspaceScaffoldWorkspaceFragment
    }
  }
`;
const WorkspaceScaffoldVersionQuery = graphql`
  query WorkspaceScaffoldVersionQuery(
    $owner: String!
    $slug: String!
    $version: Semver!
  ) {
    viewer @ifAllowed {
      ...WorkspaceVersionCloneButtonViewerFragment
    }
    workspaceBySlug(owner: $owner, slug: $slug) {
      ...WorkspaceScaffoldWorkspaceFragment
      version(version: $version) {
        ...WorkspaceScaffoldVersionFragment
      }
    }
  }
`;
const VersionFragment = graphql`
  fragment WorkspaceScaffoldVersionFragment on WorkspaceVersion {
    id
    version
    publishedAt
    latest
    viewerCanEdit: viewerCan(action: UPDATE_WORKSPACE_VERSION)
    ...WorkspaceVersionCloneButtonVersionFragment
  }
`;
const WorkspaceFragment = graphql`
  fragment WorkspaceScaffoldWorkspaceFragment on Workspace {
    id
    name
    slug
    private
    owner {
      username
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    latest: versions(first: 1)
      @connection(key: "WorkspaceScaffoldWorkspaceFragment_latest") {
      edges {
        node {
          ...WorkspaceScaffoldVersionFragment
        }
      }
    }
    viewerCanEdit: viewerCan(action: UPDATE_WORKSPACE)
    viewerCanCreateVersion: viewerCan(action: CREATE_WORKSPACE_VERSION)
    # Existence probe for the Submissions tab visibility (any submission
    # linking any version of this workspace). The per-version header action
    # reads the current version's own submissions connection instead.
    submissions(first: 1) {
      edges {
        cursor
      }
    }
    ...VoteDisplayFragment
    ...WorkspaceVersionHistoryFragment
    ...WorkspacePublishButtonFragment
  }
`;
export const loader =
  (environment: Environment) =>
  ({ params }: LoaderArgs) => {
    let res;
    res = safeParse(WorkspaceVersionPageParams, params);
    if (res.success) {
      return preload<WorkspaceScaffoldVersionQueryType>(
        environment,
        WorkspaceScaffoldVersionQuery,
        res.output,
      );
    }
    res = safeParse(WorkspacePageParams, params);
    if (res.success) {
      return preload<WorkspaceScaffoldQueryType>(
        environment,
        WorkspaceScaffoldQuery,
        res.output,
      );
    }
    throw new Error("unexpected WorkspaceScaffold params");
  };
const useWorkspaceScaffoldQuery = () => {
  const { query, ...preloaded } = usePreloaded<
    WorkspaceScaffoldQueryType | WorkspaceScaffoldVersionQueryType
  >();
  const workspace = useFragment(
    WorkspaceFragment,
    query.workspaceBySlug as WorkspaceScaffoldWorkspaceFragment$key | null,
  );
  const rawVersion =
    query.workspaceBySlug && "version" in query.workspaceBySlug
      ? query.workspaceBySlug.version
      : null;
  const version = useFragment(
    VersionFragment,
    rawVersion as WorkspaceScaffoldVersionFragment$key | null,
  );
  const latestVersion = useFragment(
    VersionFragment,
    (workspace?.latest.edges[0]?.node ??
      null) as WorkspaceScaffoldVersionFragment$key | null,
  );
  const viewer = assumeNullish(query.viewer);
  return {
    query,
    workspace,
    version,
    latestVersion,
    viewer,
    ...preloaded,
  };
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  currentUrl,
  nextUrl,
}) => defaultShouldRevalidate || currentUrl.pathname !== nextUrl.pathname;

export const lazyHandle = {
  // Used in NavItems component to make workspaces active
  workspace: true,
};
